import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getAdminGallery,
  getAdminGalleryImage,
  createGalleryImage,
  updateGalleryImage,
  deleteGalleryImage,
  AdminGalleryFilters,
} from '../api/gallery.api';
import { Gallery } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import DeleteGalleryModal from '../components/DeleteGalleryModal';

const AdminGalleryPage: React.FC = () => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeFilter, setActiveFilter] = useState<boolean | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGallery, setEditingGallery] = useState<Gallery | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; imageId: string | null; imageTitle: string }>({
    show: false,
    imageId: null,
    imageTitle: '',
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);

  const fetchGalleries = useCallback(async () => {
    setLoading(true);
    try {
      const filters: AdminGalleryFilters = {
        page: currentPage,
        limit: 10,
      };
      
      if (activeFilter !== null) {
        filters.isActive = activeFilter;
      }
      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      const response = await getAdminGallery(filters);
      
      if (response && response.data) {
        if (response.data.galleries) {
          setGalleries(Array.isArray(response.data.galleries) ? response.data.galleries : []);
          setTotalPages(response.data.totalPages || 1);
          setTotal(response.data.total || 0);
        } else {
          setGalleries([]);
          setTotalPages(1);
          setTotal(0);
        }
      } else {
        setGalleries([]);
        setTotalPages(1);
        setTotal(0);
      }
    } catch (err: any) {
      console.error('Error fetching galleries:', err);
      setGalleries([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, activeFilter, searchQuery]);

  useEffect(() => {
    if (isFilterChanging) {
      return;
    }
    fetchGalleries();
  }, [currentPage, activeFilter, searchQuery, fetchGalleries, isFilterChanging]);

  const handleOpenCreateModal = () => {
    setFormData({
      title: '',
      description: '',
    });
    setImageFile(null);
    setImagePreview(null);
    setErrors({});
    setShowCreateModal(true);
  };

  const handleOpenEditModal = async (gallery: Gallery) => {
    try {
      const response = await getAdminGalleryImage(gallery.id);
      if (response.data) {
        const galleryData = response.data;
        setFormData({
          title: galleryData.title || '',
          description: galleryData.description || '',
        });
        setImagePreview(galleryData.imageUrl || null);
        setImageFile(null);
        setErrors({});
        setEditingGallery(galleryData);
      }
    } catch (err) {
      console.error('Error fetching gallery image:', err);
    }
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setEditingGallery(null);
    setFormData({
      title: '',
      description: '',
    });
    setImageFile(null);
    setImagePreview(null);
    setErrors({});
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setErrors({ ...errors, image: 'File size exceeds 5MB limit.' });
        e.target.value = '';
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setErrors({ ...errors, image: 'Invalid file type. Please upload JPEG, PNG, GIF, or WEBP image.' });
        e.target.value = '';
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.image;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required.';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters.';
    } else if (formData.title.trim().length > 200) {
      newErrors.title = 'Title must not exceed 200 characters.';
    }

    if (formData.description && formData.description.trim().length > 5000) {
      newErrors.description = 'Description must not exceed 5000 characters.';
    }

    if (!editingGallery && !imageFile) {
      newErrors.image = 'Image is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (editingGallery) {
        const payload: any = {
          title: formData.title.trim(),
        };
        if (formData.description.trim()) {
          payload.description = formData.description.trim();
        }
        if (imageFile) {
          payload.image = imageFile;
        }
        await updateGalleryImage(editingGallery.id, payload);
      } else {
        if (!imageFile) {
          setErrors({ image: 'Image is required.' });
          setSaving(false);
          return;
        }
        await createGalleryImage({
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          image: imageFile,
        });
      }
      
      handleCloseModals();
      setTimeout(() => {
        fetchGalleries();
      }, 500);
    } catch (err: any) {
      console.error('Error saving gallery image:', err);
      if (err.response?.status === 429) {
        setErrors({ 
          general: 'Too many requests. Please wait a moment and try again.' 
        });
      } else if (err.response?.status === 400) {
        const errorData = err.response?.data;
        if (errorData?.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.keys(errorData.errors).forEach((key) => {
            const fieldMap: Record<string, string> = {
              'image_url': 'image',
            };
            const frontendKey = fieldMap[key] || key;
            let errorMessage = errorData.errors[key];
            if (Array.isArray(errorMessage)) {
              errorMessage = errorMessage[0] || errorMessage.join(', ');
            }
            mappedErrors[frontendKey] = errorMessage;
          });
          setErrors(mappedErrors);
        } else {
          setErrors({ general: errorData?.message || 'Invalid data. Please check your inputs.' });
        }
      } else if (err.response?.status === 413) {
        setErrors({ image: 'File is too large. Maximum size is 5MB.' });
      } else if (err.response?.status === 404) {
        setErrors({ general: 'Gallery image not found. It may have been deleted.' });
      } else if (err.response?.status === 403) {
        setErrors({ general: 'You do not have permission to perform this action.' });
      } else {
        setErrors({ general: err.response?.data?.message || err.message || 'Failed to save gallery image. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.imageId) return;

    try {
      await deleteGalleryImage(deleteModal.imageId);
      setDeleteModal({ show: false, imageId: null, imageTitle: '' });
      setTimeout(() => {
        fetchGalleries();
      }, 500);
    } catch (err: any) {
      console.error('Error deleting gallery image:', err);
      if (err.response?.status === 429) {
        alert('Too many requests. Please wait a moment and refresh the page.');
      } else {
        alert(err.response?.data?.message || 'Failed to delete gallery image. Please try again.');
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = async (filter: boolean | null) => {
    setIsFilterChanging(true);
    setActiveFilter(filter);
    setCurrentPage(1);
    
    setLoading(true);
    try {
      const filters: AdminGalleryFilters = {
        page: 1,
        limit: 10,
      };
      
      if (filter !== null) {
        filters.isActive = filter;
      }
      
      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      const response = await getAdminGallery(filters);
      
      if (response && response.data) {
        if (response.data.galleries) {
          setGalleries(Array.isArray(response.data.galleries) ? response.data.galleries : []);
          setTotalPages(response.data.totalPages || 1);
          setTotal(response.data.total || 0);
        }
      }
    } catch (err: any) {
      console.error('Error fetching filtered galleries:', err);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setIsFilterChanging(false);
      }, 100);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setIsFilterChanging(true);
    setTimeout(() => {
      setIsFilterChanging(false);
    }, 100);
  };

  if (loading && galleries.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">GALLERY MANAGEMENT</h1>
          <p className="text-muted-soft mb-0">Upload, edit, and manage gallery images</p>
        </div>
        <div className="create-notice-btn-container" style={{ flexShrink: 0, minWidth: 'fit-content', display: 'flex' }}>
          <button 
            className="btn btn-arena create-notice-btn" 
            onClick={handleOpenCreateModal}
            style={{ 
              display: 'inline-flex',
              visibility: 'visible',
              opacity: 1,
              position: 'relative',
              zIndex: 10,
              minWidth: '150px'
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Upload Image
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card card-surface mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search images by title or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="col-12 col-md-6">
                <button type="submit" className="btn btn-outline-secondary w-100">
                  <i className="bi bi-search me-2"></i>
                  Search
                </button>
              </div>
            </div>
          </form>
          
          <div className="d-flex flex-column flex-md-row flex-wrap gap-2 align-items-start align-items-md-center">
            <span className="fw-semibold mb-2 mb-md-0">Filter by Status:</span>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-sm ${activeFilter === null ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange(null)}
              >
                All Images
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeFilter === true ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange(true)}
              >
                Active
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeFilter === false ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange(false)}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery List */}
      <div className="card card-surface">
        <div className="card-body">
          {loading ? (
            <LoadingSpinner />
          ) : galleries.length === 0 ? (
            <div className="alert alert-secondary mb-0">No gallery images found.</div>
          ) : (
            <div className="row g-4">
              {galleries.map((gallery) => (
                <div key={gallery.id} className="col-12 col-md-6 col-lg-4">
                  <div className="card card-surface h-100">
                    <div 
                      className="position-relative" 
                      style={{ 
                        minHeight: '200px',
                        maxHeight: '350px',
                        aspectRatio: '4/3',
                        overflow: 'hidden',
                        backgroundColor: '#0a0a0a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img
                        src={gallery.imageUrl}
                        alt={gallery.title}
                        className="card-img-top"
                        style={{
                          width: '100%',
                          height: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%230a0a0a" width="400" height="200"/%3E%3Ctext fill="%23ffffff" font-family="Arial" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage Not Available%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      <span className={`badge position-absolute top-0 end-0 m-2 ${gallery.isActive !== false ? 'bg-success' : 'bg-danger'}`}>
                        {gallery.isActive !== false ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                    <div className="card-body d-flex flex-column">
                      <h6 className="card-title mb-2" style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
                        {gallery.title}
                      </h6>
                      {gallery.description && (
                        <p className="text-muted-soft small flex-grow-1 mb-2" style={{ fontSize: '0.875rem' }}>
                          {gallery.description.length > 100 
                            ? `${gallery.description.substring(0, 100)}...` 
                            : gallery.description}
                        </p>
                      )}
                      <div className="d-flex gap-2 mt-auto">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary flex-fill"
                          onClick={() => handleOpenEditModal(gallery)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger flex-fill"
                          onClick={() =>
                            setDeleteModal({
                              show: true,
                              imageId: gallery.id,
                              imageTitle: gallery.title,
                            })
                          }
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav aria-label="Page navigation" className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
            </li>
          </ul>
          <div className="text-center mt-2 text-muted-soft small">
            Showing {galleries.length} of {total} image{total !== 1 ? 's' : ''}
          </div>
        </nav>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingGallery) && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseModals}
            style={{ zIndex: 1040 }}
          ></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', overflow: 'auto' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" style={{ margin: '1rem', maxWidth: '700px' }}>
              <div className="modal-content card-surface" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">{editingGallery ? 'Edit Gallery Image' : 'Upload Gallery Image'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModals}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                    {errors.general && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {errors.general}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => {
                            const newErrors = { ...errors };
                            delete newErrors.general;
                            setErrors(newErrors);
                          }}
                          aria-label="Close"
                        ></button>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">
                        Image <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex flex-column flex-md-row align-items-start gap-3">
                        {imagePreview && (
                          <div className="flex-shrink-0">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="rounded"
                              style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                            />
                          </div>
                        )}
                        <div className="flex-grow-1">
                          <input
                            type="file"
                            className={`form-control ${errors.image ? 'is-invalid' : ''}`}
                            accept="image/*"
                            onChange={handleFileChange}
                            disabled={saving}
                          />
                          {errors.image && <div className="invalid-feedback">{errors.image}</div>}
                          <div className="form-text">Max 5MB. Formats: JPEG, PNG, GIF, WEBP{editingGallery && '. Leave empty to keep current image.'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                        value={formData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        disabled={saving}
                        maxLength={200}
                        placeholder="Enter image title"
                      />
                      {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                      <div className="form-text">3-200 characters</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Description
                      </label>
                      <textarea
                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                        rows={5}
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        disabled={saving}
                        maxLength={5000}
                        placeholder="Enter image description (optional)"
                      />
                      {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                      <div className="form-text">Max 5000 characters</div>
                    </div>
                  </div>
                  <div className="modal-footer border-top d-flex flex-column flex-sm-row gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary modal-footer-btn"
                      onClick={handleCloseModals}
                      disabled={saving}
                      style={{
                        display: 'inline-flex',
                        visibility: 'visible',
                        opacity: 1,
                        minWidth: '100px'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-arena modal-footer-btn" 
                      disabled={saving}
                      style={{
                        display: 'inline-flex',
                        visibility: 'visible',
                        opacity: 1,
                        minWidth: '150px'
                      }}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        editingGallery ? 'Update Image' : 'Upload Image'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      <DeleteGalleryModal
        show={deleteModal.show}
        imageTitle={deleteModal.imageTitle}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ show: false, imageId: null, imageTitle: '' })}
      />
    </div>
  );
};

export default AdminGalleryPage;
